import {
  type DatabaseBlockDataSource,
  databaseViewInitTemplate,
  provisionShardedDatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import {
  type Doc,
  type ExtensionType,
  type Store,
  Text,
  type Workspace,
} from '@blocksuite/affine/store';
import { parseXYWH, serializeXYWH } from '@blocksuite/global/gfx';
import {
  type CanvasPlacementRect,
  DEFAULT_CANVAS_DATABASE_HEIGHT,
  DEFAULT_CANVAS_DATABASE_WIDTH,
  DEFAULT_CANVAS_PLACEMENT_GAP,
  findFreeCanvasRowRects,
} from '@streamlain/database-identity';

import type {
  DatabaseImportColumnPayload,
  DatabaseImportRowPayload,
  DatabaseImportSourcePayload,
} from './database-import-client';
import type { DatabaseImportSourceMetadata } from './database-import-payload';

type SourceWithLayout = DatabaseImportSourcePayload & {
  width?: number;
  height?: number;
};

type SourceLayoutInput = {
  x: number;
  y: number;
  width?: number;
  height?: number;
};

export type LocalShardedDatabaseImportSourceMetadata =
  DatabaseImportSourceMetadata;

export type LocalShardedDatabaseImportRowBatch = {
  clientId: string;
  rows: DatabaseImportRowPayload[];
};

export type LocalShardedDatabaseImportOptions = {
  collection: Workspace;
  docId: string;
  sources: SourceWithLayout[];
  extensions?: ExtensionType[];
  docName?: string;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
};

export type LocalShardedDatabaseImportResult = {
  docId: string;
  doc: Doc;
  stats: {
    databaseCount: number;
    totalRows: number;
  };
  databaseBlockIds: string[];
};

export type LocalShardedDatabaseImportFromBatchesOptions = Omit<
  LocalShardedDatabaseImportOptions,
  'sources'
> & {
  sources: LocalShardedDatabaseImportSourceMetadata[];
  readNextBatch: () => Promise<LocalShardedDatabaseImportRowBatch | null>;
};

export type LocalShardedDatabaseImportProgressPhase =
  | 'csv-reading'
  | 'excel-reading'
  | 'notion-reading'
  | 'databases-preparing'
  | 'rows-importing'
  | 'import-finalizing'
  | 'import-cancelling';

export type LocalShardedDatabaseImportProgress = {
  phase: LocalShardedDatabaseImportProgressPhase;
  processedRows: number;
  totalRows: number;
  percent: number | null;
};

export type LocalShardedDatabaseImportProgressChange = (
  progress: LocalShardedDatabaseImportProgress
) => void;

const TITLE_COLUMN_ID = 'title';
const PROGRESS_UPDATE_ROW_INTERVAL = 25;

type BlockModelWithGeometry = {
  props?: { xywh?: unknown };
  yBlock?: { get?: (key: string) => unknown };
  xywh?: unknown;
};

function isLoadedImportTargetDoc(doc: Doc): boolean {
  return (doc as Doc & { ready?: boolean }).ready === true;
}

function progressPercent(processedRows: number, totalRows: number) {
  if (totalRows <= 0) return null;
  return Math.min(100, Math.floor((processedRows / totalRows) * 100));
}

function yieldToBrowser(): Promise<void> {
  return new Promise(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
      return;
    }
    setTimeout(resolve, 0);
  });
}

function toCanvasPlacementRect(value: unknown): CanvasPlacementRect | null {
  const xywh = parseXYWH(value);
  if (!xywh || xywh[2] <= 0 || xywh[3] <= 0) return null;
  return [xywh[0], xywh[1], xywh[2], xywh[3]];
}

function readCanvasBlockRect(model: unknown): CanvasPlacementRect | null {
  if (!model || typeof model !== 'object') return null;
  const block = model as BlockModelWithGeometry;
  return (
    toCanvasPlacementRect(block.xywh) ??
    toCanvasPlacementRect(block.props?.xywh) ??
    toCanvasPlacementRect(block.yBlock?.get?.('prop:xywh'))
  );
}

function collectOccupiedCanvasBlockRects(store: Store): CanvasPlacementRect[] {
  const root = store.root as { children?: unknown[] } | null;
  const occupied: CanvasPlacementRect[] = [];
  for (const child of root?.children ?? []) {
    const rect = readCanvasBlockRect(child);
    if (rect) occupied.push(rect);
  }
  return occupied;
}

function preferredImportPosition(
  source: SourceLayoutInput
): { x: number; y: number } | undefined {
  if (!Number.isFinite(source.x) || !Number.isFinite(source.y)) {
    return undefined;
  }
  return { x: source.x, y: source.y };
}

function resolveImportSourceCanvasRects(
  sources: readonly SourceLayoutInput[],
  occupied: CanvasPlacementRect[]
): CanvasPlacementRect[] {
  const rects = findFreeCanvasRowRects({
    occupied,
    items: sources.map(source => ({
      width: source.width ?? DEFAULT_CANVAS_DATABASE_WIDTH,
      height: source.height ?? DEFAULT_CANVAS_DATABASE_HEIGHT,
    })),
    gap: DEFAULT_CANVAS_PLACEMENT_GAP,
    preferred: sources[0] ? preferredImportPosition(sources[0]) : undefined,
  });
  occupied.push(...rects);
  return rects;
}

export function throwIfLocalDatabaseImportAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  const error = new Error('Import was cancelled.');
  error.name = 'AbortError';
  throw error;
}

function ensureHostStore(input: {
  collection: Workspace;
  docId: string;
  docName?: string;
  extensions?: ExtensionType[];
}): { doc: Doc; store: Store; parentId: string } {
  const doc =
    input.collection.getDoc(input.docId) ??
    input.collection.createDoc(input.docId);
  const store = doc.getStore({
    id: input.docId,
    ...(input.extensions ? { extensions: input.extensions } : {}),
  });
  if (!isLoadedImportTargetDoc(doc)) {
    store.load?.();
  }

  if (!store.root) {
    const parentId = store.addBlock('affine:page', {
      title: new Text(input.docName ?? ''),
    });
    store.addBlock('affine:surface' as never, {}, parentId);
  }

  const parentId = store.root?.id;
  if (!parentId) {
    throw new Error('Database import target document has no root block.');
  }

  return { doc, store, parentId };
}

type SourceMetadataWithLayout =
  | SourceWithLayout
  | LocalShardedDatabaseImportSourceMetadata;

function databaseIdsByClientId(
  store: Store,
  sources: readonly SourceMetadataWithLayout[]
): Map<string, string> {
  const ids = new Map<string, string>();
  for (const source of sources) {
    if (ids.has(source.clientId)) {
      throw new Error(
        `Duplicate database import clientId "${source.clientId}"`
      );
    }
    ids.set(source.clientId, store.workspace.idGenerator());
  }
  return ids;
}

function resolveColumnData(
  column: DatabaseImportColumnPayload,
  databaseIdByClientId: ReadonlyMap<string, string>
): Record<string, unknown> | undefined {
  if (!column.data) return undefined;
  const data = { ...column.data };
  if (column.type !== 'relation') return data;

  const targetClientId =
    typeof data.targetDatabaseClientId === 'string'
      ? data.targetDatabaseClientId
      : undefined;
  const targetDatabaseId =
    typeof data.targetDatabaseId === 'string'
      ? data.targetDatabaseId
      : targetClientId
        ? databaseIdByClientId.get(targetClientId)
        : undefined;
  if (!targetDatabaseId) {
    throw new Error(
      `Relation column "${column.name}" is missing targetDatabaseId or targetDatabaseClientId.`
    );
  }

  delete data.targetDatabaseClientId;
  return {
    ...data,
    targetDatabaseId,
    targetFieldId:
      typeof data.targetFieldId === 'string'
        ? data.targetFieldId
        : TITLE_COLUMN_ID,
    cardinality:
      data.cardinality === 'one' || data.cardinality === 'many'
        ? data.cardinality
        : 'many',
  };
}

function configureColumns(input: {
  dataSource: DatabaseBlockDataSource;
  source: SourceMetadataWithLayout;
  databaseIdByClientId: ReadonlyMap<string, string>;
}): Map<string, string> {
  const columnIds = new Map<string, string>();
  columnIds.set(input.source.titleColumnId, TITLE_COLUMN_ID);

  for (const column of input.source.columns) {
    if (column.id === input.source.titleColumnId) continue;
    const propertyId = input.dataSource.propertyAdd('end', {
      type: column.type,
      name: column.name,
    });
    if (!propertyId) {
      throw new Error(
        `Database import cannot create column "${column.name}" of type "${column.type}".`
      );
    }
    columnIds.set(column.id, propertyId);

    const data = resolveColumnData(column, input.databaseIdByClientId);
    if (data) {
      input.dataSource.propertyDataSet(propertyId, data);
    }
  }

  return columnIds;
}

function extractCellValue(value: unknown): unknown {
  if (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'value' in value
  ) {
    return (value as { value: unknown }).value;
  }
  return value;
}

function textValue(value: unknown): string {
  const raw = extractCellValue(value);
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (
    raw &&
    typeof raw === 'object' &&
    '$blocksuite:internal:text$' in raw &&
    Array.isArray((raw as { delta?: unknown }).delta)
  ) {
    return (raw as unknown as { delta: Array<{ insert?: unknown }> }).delta
      .map(delta => String(delta.insert ?? ''))
      .join('');
  }
  if (typeof (raw as { toString?: unknown }).toString === 'function') {
    return String(raw);
  }
  return '';
}

function valueForProperty(input: {
  propertyId: string;
  propertyType: string | undefined;
  value: unknown;
}): unknown {
  if (
    input.propertyId === TITLE_COLUMN_ID ||
    input.propertyType === 'title' ||
    input.propertyType === 'rich-text'
  ) {
    return textValue(input.value);
  }
  return extractCellValue(input.value);
}

async function writeRowBatch(input: {
  dataSource: DatabaseBlockDataSource;
  rows: readonly DatabaseImportRowPayload[];
  columnIds: ReadonlyMap<string, string>;
  totalRows: number;
  processedRows: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
}): Promise<number> {
  let processedRows = input.processedRows;
  const rowInserts = input.rows.map(row => {
    throwIfLocalDatabaseImportAborted(input.signal);
    const values: Record<string, unknown> = {};
    for (const [sourceColumnId, rawValue] of Object.entries(row.values)) {
      const propertyId = input.columnIds.get(sourceColumnId);
      if (!propertyId) {
        throw new Error(
          `Row "${row.id}" references unknown import column "${sourceColumnId}".`
        );
      }
      values[propertyId] = valueForProperty({
        propertyId,
        propertyType: input.dataSource.propertyTypeGet(propertyId),
        value: rawValue,
      });
    }
    return { rowId: row.id, values };
  });

  input.dataSource.rowAddManyWithCellValues(rowInserts, 'end');

  let remainingRows = input.rows.length;
  while (remainingRows > 0) {
    remainingRows -= 1;
    processedRows += 1;
    if (
      input.onProgress &&
      (processedRows === input.totalRows ||
        processedRows % PROGRESS_UPDATE_ROW_INTERVAL === 0)
    ) {
      input.onProgress({
        phase: 'rows-importing',
        processedRows,
        totalRows: input.totalRows,
        percent: progressPercent(processedRows, input.totalRows),
      });
      await yieldToBrowser();
      throwIfLocalDatabaseImportAborted(input.signal);
    }
  }
  return input.rows.length;
}

async function writeRows(input: {
  dataSource: DatabaseBlockDataSource;
  source: DatabaseImportSourcePayload;
  columnIds: ReadonlyMap<string, string>;
  totalRows: number;
  processedRows: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
}): Promise<number> {
  return writeRowBatch({
    dataSource: input.dataSource,
    rows: input.source.rows,
    columnIds: input.columnIds,
    totalRows: input.totalRows,
    processedRows: input.processedRows,
    onProgress: input.onProgress,
    signal: input.signal,
  });
}

export async function importShardedDatabasesLocalFirst(
  options: LocalShardedDatabaseImportOptions
): Promise<LocalShardedDatabaseImportResult> {
  throwIfLocalDatabaseImportAborted(options.signal);
  const { doc, store, parentId } = ensureHostStore(options);
  throwIfLocalDatabaseImportAborted(options.signal);
  const databaseIdByClientId = databaseIdsByClientId(store, options.sources);
  const databaseBlockIds: string[] = [];
  const totalImportRows = options.sources.reduce(
    (sum, source) => sum + source.rows.length,
    0
  );
  let totalRows = 0;
  let processedRows = 0;
  const occupiedRects = collectOccupiedCanvasBlockRects(store);
  const importRects = resolveImportSourceCanvasRects(
    options.sources,
    occupiedRects
  );

  options.onProgress?.({
    phase: 'databases-preparing',
    processedRows: 0,
    totalRows: totalImportRows,
    percent: progressPercent(0, totalImportRows),
  });
  await yieldToBrowser();
  throwIfLocalDatabaseImportAborted(options.signal);

  for (const [sourceIndex, source] of options.sources.entries()) {
    throwIfLocalDatabaseImportAborted(options.signal);
    const databaseId = databaseIdByClientId.get(source.clientId);
    if (!databaseId) {
      throw new Error(`Database import clientId "${source.clientId}" missing.`);
    }
    const [x, y, width, height] = importRects[sourceIndex]!;
    const provisioned = provisionShardedDatabaseBlockDataSource(store, {
      databaseId,
      title: source.title,
      parentId,
      props: {
        xywh: serializeXYWH(x, y, width, height),
      },
      initDataSource: dataSource =>
        databaseViewInitTemplate(dataSource, 'table'),
    });

    const columnIds = configureColumns({
      dataSource: provisioned.dataSource,
      source,
      databaseIdByClientId,
    });
    const sourceRows = await writeRows({
      dataSource: provisioned.dataSource,
      source,
      columnIds,
      totalRows: totalImportRows,
      processedRows,
      onProgress: options.onProgress,
      signal: options.signal,
    });
    totalRows += sourceRows;
    processedRows += sourceRows;
    databaseBlockIds.push(provisioned.databaseBlockId);
  }

  throwIfLocalDatabaseImportAborted(options.signal);
  options.onProgress?.({
    phase: 'import-finalizing',
    processedRows: totalImportRows,
    totalRows: totalImportRows,
    percent: 100,
  });

  return {
    docId: options.docId,
    doc,
    stats: {
      databaseCount: options.sources.length,
      totalRows,
    },
    databaseBlockIds,
  };
}

export async function importShardedDatabasesLocalFirstFromBatches(
  options: LocalShardedDatabaseImportFromBatchesOptions
): Promise<LocalShardedDatabaseImportResult> {
  throwIfLocalDatabaseImportAborted(options.signal);
  const { doc, store, parentId } = ensureHostStore(options);
  throwIfLocalDatabaseImportAborted(options.signal);
  const databaseIdByClientId = databaseIdsByClientId(store, options.sources);
  const databaseBlockIds: string[] = [];
  const totalImportRows = options.sources.reduce(
    (sum, source) => sum + source.rowCount,
    0
  );
  const dataSourcesByClientId = new Map<
    string,
    {
      dataSource: DatabaseBlockDataSource;
      columnIds: Map<string, string>;
    }
  >();
  let totalRows = 0;
  let processedRows = 0;
  const occupiedRects = collectOccupiedCanvasBlockRects(store);
  const importRects = resolveImportSourceCanvasRects(
    options.sources,
    occupiedRects
  );

  options.onProgress?.({
    phase: 'databases-preparing',
    processedRows: 0,
    totalRows: totalImportRows,
    percent: progressPercent(0, totalImportRows),
  });
  await yieldToBrowser();
  throwIfLocalDatabaseImportAborted(options.signal);

  for (const [sourceIndex, source] of options.sources.entries()) {
    throwIfLocalDatabaseImportAborted(options.signal);
    const databaseId = databaseIdByClientId.get(source.clientId);
    if (!databaseId) {
      throw new Error(`Database import clientId "${source.clientId}" missing.`);
    }
    const [x, y, width, height] = importRects[sourceIndex]!;
    const provisioned = provisionShardedDatabaseBlockDataSource(store, {
      databaseId,
      title: source.title,
      parentId,
      props: {
        xywh: serializeXYWH(x, y, width, height),
      },
      initDataSource: dataSource =>
        databaseViewInitTemplate(dataSource, 'table'),
    });

    const columnIds = configureColumns({
      dataSource: provisioned.dataSource,
      source,
      databaseIdByClientId,
    });
    dataSourcesByClientId.set(source.clientId, {
      dataSource: provisioned.dataSource,
      columnIds,
    });
    databaseBlockIds.push(provisioned.databaseBlockId);
  }

  while (true) {
    throwIfLocalDatabaseImportAborted(options.signal);
    const batch = await options.readNextBatch();
    throwIfLocalDatabaseImportAborted(options.signal);
    if (!batch) break;
    if (batch.rows.length === 0) continue;

    const target = dataSourcesByClientId.get(batch.clientId);
    if (!target) {
      throw new Error(
        `Database import row batch references unknown clientId "${batch.clientId}".`
      );
    }

    const writtenRows = await writeRowBatch({
      dataSource: target.dataSource,
      rows: batch.rows,
      columnIds: target.columnIds,
      totalRows: totalImportRows,
      processedRows,
      onProgress: options.onProgress,
      signal: options.signal,
    });
    totalRows += writtenRows;
    processedRows += writtenRows;
  }

  throwIfLocalDatabaseImportAborted(options.signal);
  options.onProgress?.({
    phase: 'import-finalizing',
    processedRows: totalImportRows,
    totalRows: totalImportRows,
    percent: 100,
  });

  return {
    docId: options.docId,
    doc,
    stats: {
      databaseCount: options.sources.length,
      totalRows,
    },
    databaseBlockIds,
  };
}
